import { inspect, formatLogArgs } from './inspect'

/**
 * The Worker's bootstrap, assembled as source text.
 *
 * `inspect` and `formatLogArgs` are injected via toString() rather than
 * rewritten here, so the formatter that decides what output looks like is the
 * same one the tests cover. See the constraint note in inspect.ts.
 */
export function buildWorkerSource(): string {
  return `
'use strict';

// Remove every way out before any learner code is parsed. Deleting a global on
// a Worker's scope genuinely removes it — this is not shadowing.
;(function stripCapabilities() {
  var doomed = [
    'fetch', 'XMLHttpRequest', 'WebSocket', 'EventSource', 'importScripts',
    'Worker', 'SharedWorker', 'BroadcastChannel', 'indexedDB', 'caches',
    'navigator', 'Notification', 'RTCPeerConnection'
  ];
  for (var i = 0; i < doomed.length; i++) {
    try { delete self[doomed[i]]; } catch (e) { /* frozen in some engines */ }
    try {
      Object.defineProperty(self, doomed[i], {
        get: function () { throw new Error('The sandbox has no network access.'); },
        configurable: false
      });
    } catch (e) { /* already gone */ }
  }
})();

var inspect = ${inspect.toString()};
var formatLogArgs = ${formatLogArgs.toString()};
function __fmt(v) { return inspect(v, 0, []); }
function __line(a) { return formatLogArgs(Array.prototype.slice.call(a), __fmt); }

var __out = [];
function __emit(line) {
  // A runaway loop that logs can produce output faster than it can be sent.
  // Cap it, tell the truth about capping, and stop — an unbounded array here
  // is how the tab runs out of memory before the timeout can fire.
  if (__out.length === 500) { __out.push('… output truncated at 500 lines'); return; }
  if (__out.length > 500) return;
  __out.push(line);
}

var console = {
  log:   function () { __emit(__line(arguments)); },
  info:  function () { __emit(__line(arguments)); },
  warn:  function () { __emit(__line(arguments)); },
  debug: function () { __emit(__line(arguments)); },
  error: function () { __emit(__line(arguments)); },
  table: function () { __emit(__line(arguments)); }
};

// A rejected promise that nobody awaits would otherwise reach the Worker's
// error handler and be reported as if the snippet had failed. It has not:
// Lesson B1.9 runs exactly this on purpose to show that the error disappears
// with nobody listening. Suppressing it keeps that demonstration truthful, and
// keeps this in step with scripts/lint-content.ts, which measures the answer
// keys and must observe identical output.
self.addEventListener('unhandledrejection', function (e) { e.preventDefault(); });

self.onmessage = function (event) {
  var source = event.data && event.data.source;
  var error = null;
  try {
    // Indirect eval, so the snippet runs in global scope and \`var\` and
    // function declarations behave the way the lesson says they do.
    (0, eval)(source);
  } catch (e) {
    error = (e && e.name ? e.name + ': ' : '') + (e && e.message ? e.message : String(e));
  }

  // Give queued microtasks and timers one turn to flush, so a snippet about
  // the event loop can actually demonstrate the event loop. This is why the
  // lessons on ordering work at all.
  setTimeout(function () {
    self.postMessage({ output: __out, error: error });
  }, 0);
};
`
}
